import type { SignIn } from '@logto/schemas';
import { InteractionEvent, SignInIdentifier } from '@logto/schemas';
import { HTTPError } from 'ky';
import { useCallback, useContext, useState } from 'react';

import api from '@/apis/api';
import { initInteraction } from '@/apis/experience';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import useCheckSingleSignOn from '@/hooks/use-check-single-sign-on';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import useSendVerificationCode from '@/hooks/use-send-verification-code';
import { useSieMethods } from '@/hooks/use-sie';
import { UserFlow } from '@/types';
import { experienceApiRoutes } from '@/apis/experience/const';

type ContactValidationResponse = {
  active: boolean;
  message?: string;
};

// Portal API base URL. In built bundles (stage/prod) it's derived from the
// current hostname (auth.<env>.enabledental.com → app.<env>.enabledental.com/api),
// so one bundle works for every deployed env. The localhost branch is only
// reachable under `vite dev` — import.meta.env.DEV is replaced at build time,
// so the local URL is dead-code-eliminated out of production builds.
const getPortalApiBase = (): string => {
  if (import.meta.env.DEV) {
    return 'http://localhost:8001';
  }
  return `https://${window.location.hostname.replace(/^auth\./, 'app.')}/api`;
};

const validateEmailContact = async (email: string): Promise<ContactValidationResponse> => {
  const response = await fetch(`${getPortalApiBase()}/portal/auth/email_lookup/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    let errorMessage = 'Validation request failed';
    try {
      const errorData = (await response.json()) as { detail?: string };
      if (errorData.detail) {
        errorMessage = errorData.detail;
      }
    } catch {
    }
    const error = new Error(errorMessage);
    (error as Error & { detail?: string }).detail = errorMessage;
    throw error;
  }

  return response.json() as Promise<ContactValidationResponse>;
};

const checkUserHasPassword = async (email: string): Promise<boolean> => {
  try {
    const response = await api.get(`${experienceApiRoutes.prefix}/check-password-by-email`, {
      searchParams: { email },
    });

    const result = await response.json<{ hasPassword: boolean }>();
    return result.hasPassword;
  } catch (error) {
    // If user not found or error, assume password exists (fallback to default behavior)
    console.error('Failed to check password:', error);
    return true;
  }
};

const useOnSubmit = (signInMethods: SignIn['methods']) => {
  const navigate = useNavigateWithPreservedSearchParams();
  const { ssoConnectors } = useSieMethods();
  const { onSubmit: checkSingleSignOn } = useCheckSingleSignOn();
  const { setIdentifierInputValue } = useContext(UserInteractionContext);
  const [validationError, setValidationError] = useState<string>();

  const navigateToPasswordPage = useCallback(() => {
    navigate({
      pathname: `/${UserFlow.SignIn}/password`,
    });
  }, [navigate]);

  const {
    errorMessage,
    clearErrorMessage,
    onSubmit: sendVerificationCode,
  } = useSendVerificationCode(UserFlow.SignIn);

  const clearValidationError = useCallback(() => {
    setValidationError(undefined);
  }, []);

  const onSubmit = useCallback(
    async (identifier: SignInIdentifier, value: string) => {
      const method = signInMethods.find((method) => method.identifier === identifier);

      if (!method) {
        throw new Error(`Cannot find method with identifier type ${identifier}`);
      }

      setIdentifierInputValue({ type: identifier, value });

      const { password, isPasswordPrimary, verificationCode } = method;

      if (identifier === SignInIdentifier.Username) {
        navigateToPasswordPage();

        return;
      }

      // Validate email through external API before proceeding
      let userHasPassword: boolean | undefined;
      if (identifier === SignInIdentifier.Email) {
        setValidationError(undefined);
        try {
          const validationResult = await validateEmailContact(value);
          if (!validationResult.active) {
            setValidationError('User does not exist or is not active.');
            return;
          }

          // Initialize interaction session before checking password
          await initInteraction(InteractionEvent.SignIn);

          // Check if user has password in Logto
          userHasPassword = await checkUserHasPassword(value);
        } catch (error) {
          console.error('Email validation failed:', error);
          let errorDetail: string | undefined;

          if (error instanceof HTTPError) {
            try {
              const errorData = await error.response.json<{ detail?: string }>();
              errorDetail = errorData.detail;
            } catch {
            }
          } else if (error instanceof Error) {
            errorDetail = (error as Error & { detail?: string }).detail;
          }

          if (errorDetail) {
            setValidationError(errorDetail);
          } else {
            setValidationError('User does not exist or is not active.');
          }
          return;
        }
      }

      // Check if the email is registered with any SSO connectors. If the email is registered with any SSO connectors, we should not proceed to the next step
      if (identifier === SignInIdentifier.Email && ssoConnectors.length > 0) {
        const result = await checkSingleSignOn(value);

        if (result) {
          return;
        }
      }

      // If user doesn't have password and verification code is available, use verification code
      if (identifier === SignInIdentifier.Email && userHasPassword === false && verificationCode) {
        await sendVerificationCode({ identifier, value });
        return;
      }

      // If password is available and (primary or no verification code), use password
      if (password && (isPasswordPrimary || !verificationCode)) {
        navigateToPasswordPage();

        return;
      }

      // Otherwise use verification code if available
      if (verificationCode) {
        await sendVerificationCode({ identifier, value });
      }
    },
    [
      signInMethods,
      setIdentifierInputValue,
      ssoConnectors.length,
      navigateToPasswordPage,
      checkSingleSignOn,
      sendVerificationCode,
    ]
  );

  return {
    errorMessage: errorMessage || validationError,
    clearErrorMessage: () => {
      clearErrorMessage();
      clearValidationError();
    },
    onSubmit,
  };
};

export default useOnSubmit;
